import React, { useState } from "react";
import { motion } from "framer-motion";

const pricingTiers = [
  { label: "150–300", range: "150–300 patients/mo", price: 15 },
  { label: "301–500", range: "301–500 patients/mo", price: 12 },
  { label: "501–800", range: "501–800 patients/mo", price: 8 },
];

const aiProducts = [
  "AI Receptionist", "AI Hygiene Recall", "AI Case Acceptance", "AI Insurance Verification",
  "AI Claim Scrubbing", "AI Denial Management", "AI Patient Billing", "AI AR & Collections",
  "AI EOB Posting", "AI Insurance Calls", "AI Schedule Optimizer", "AI Patient Intake",
  "AI Practice Manager", "AI Patient Experience", "AI Fax Manager",
  "AI Copilot", "AI Scribe", "AI Treatment Planner", "AI Revenue Analytics", "AI Claim Submission",
];

const softwareItems = [
  "Practice Management System",
  "Cloud Imaging (SOTA)",
  "Communication Suite & VoIP",
  "Marketing & Campaigns",
  "eClaims Portal",
  "Revenue Intelligence Suite",
  "Insurance Verification Software",
  "Integrated Payments Suite",
  "Patient Portal",
  "ePrescribing (Coming Soon)",
];

const getInitialTierIndex = (patients) => {
  if (patients <= 300) return 0;
  if (patients <= 500) return 1;
  return 2;
};

const QuoteEngagementModelsSlide = ({
  patients,
  planType,
  fixedPrice,
  fixedPriceMaxCollections,
  fixedPriceMaxPatients,
  waiveSoftwareFeeFirst12Months,
  softwareFreeAfter12Months,
  collections,
  ashaPrice: precomputedPrice,
  ashaPriceLabel: precomputedLabel,
}) => {
  const [sliderIndex, setSliderIndex] = useState(getInitialTierIndex(patients));
  const current = pricingTiers[sliderIndex];
  const isManaged = planType === "managed";
  const isFixed = planType === "fixed" && fixedPrice && fixedPrice > 0;
  const fixedPerPatient = isFixed && fixedPriceMaxPatients ? (fixedPrice / fixedPriceMaxPatients).toFixed(2) : null;
  const softwareFeeWaivedFirst12Months = softwareFreeAfter12Months || (
    typeof waiveSoftwareFeeFirst12Months === "boolean" ? waiveSoftwareFeeFirst12Months : true
  );
  const softwarePricingNote = softwareFreeAfter12Months
    ? "Included free permanently on this plan"
    : softwareFeeWaivedFirst12Months
      ? "Waived for your first 12 months, then $799/mo"
      : "Applies from month one";

  return (
    <div className="h-full w-full bg-[#fafbfc] flex flex-col p-5 xl:p-6 relative overflow-hidden font-sans">
      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: -15 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-3 flex-shrink-0"
      >
        <h2 className="text-4xl lg:text-[46px] font-bold text-slate-900 tracking-tight leading-tight">
          Unparalleled technology.{" "}
          <span className="text-blue-600">Unbeatable pricing.</span>
        </h2>
      </motion.div>

      {/* Content area */}
      <div className="flex-1 min-h-0 flex justify-center gap-3">
        {/* Main Card — shown for all plan types */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className={`relative bg-white rounded-2xl shadow-[0_4px_40px_rgba(0,0,0,0.06)] border border-slate-100 flex flex-col min-h-0 w-full ${isManaged ? 'max-w-[720px]' : 'max-w-[960px]'}`}
          >
            <div className="grid grid-cols-2 flex-1 min-h-0" style={{ gridTemplateRows: 'auto auto auto 1fr' }}>
              {/* Row 1: Headers */}
              <div className="px-5 pt-4 pb-1 border-r border-slate-100">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <h3 className="text-lg font-bold text-slate-900">AI Cost</h3>
                </div>
                <p className="text-[13px] font-semibold text-slate-700 leading-snug">
                  20 AI products to do the work your staff does — faster, and more accurately.
                </p>
              </div>
              <div className="px-5 pt-4 pb-1">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <h3 className="text-lg font-bold text-slate-900">Software Cost</h3>
                </div>
                <p className="text-[13px] font-semibold text-slate-700 leading-snug">
                  10 softwares to run your practice, consolidated into one AI-native platform.
                </p>
              </div>

              {/* Row 2: Pricing */}
              <div className="px-5 py-3 border-r border-slate-100">
                {isManaged ? (
                  <div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-3xl font-extrabold text-slate-900 tracking-tight">3%</span>
                      <span className="text-sm text-slate-500">of collections</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1.5">
                      US-based dental billing expert with 8+ years experience works alongside your AI team
                    </p>
                  </div>
                ) : isFixed ? (
                  <div>
                    <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider mb-1.5 border border-emerald-200">
                      Custom Offer
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-3xl font-extrabold text-slate-900 tracking-tight">
                        ${fixedPrice.toLocaleString()}
                      </span>
                      <span className="text-sm text-slate-500">/mo</span>
                      {fixedPerPatient && (
                        <>
                          <span className="text-lg text-slate-300 mx-0.5">=</span>
                          <span className="text-3xl font-extrabold text-slate-900 tracking-tight">
                            ${fixedPerPatient}
                          </span>
                          <span className="text-sm text-slate-500">/patient</span>
                        </>
                      )}
                    </div>
                    {(fixedPriceMaxCollections || fixedPriceMaxPatients) && (
                      <div className="flex items-center gap-3 mt-1.5">
                        {fixedPriceMaxPatients && (
                          <span className="text-[10px] text-slate-500">
                            Up to <strong className="text-slate-700">{fixedPriceMaxPatients.toLocaleString()} patients/mo</strong>
                          </span>
                        )}
                        {fixedPriceMaxCollections && (
                          <span className="text-[10px] text-slate-500">
                            Up to <strong className="text-slate-700">${(fixedPriceMaxCollections / 1000).toFixed(0)}K collections/mo</strong>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold text-slate-600">{current.range}</span>
                      <div className="text-right">
                        <span className="text-2xl font-extrabold text-slate-900 tracking-tight">${current.price}</span>
                        <span className="text-xs text-slate-500 ml-1">/patient</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={pricingTiers.length - 1}
                      step={1}
                      value={sliderIndex}
                      onChange={(e) => setSliderIndex(Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-100 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white"
                    />
                    <div className="flex justify-between mt-0.5">
                      {pricingTiers.map((tier, i) => (
                        <span
                          key={i}
                          className={`text-[10px] cursor-pointer ${i === sliderIndex ? "text-blue-600 font-semibold" : "text-slate-400"}`}
                          onClick={() => setSliderIndex(i)}
                        >
                          {tier.label} pts
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="px-5 py-3 flex flex-col justify-center">
                {isManaged ? (
                  <>
                    <div className="flex items-baseline">
                      <span className="text-2xl font-extrabold text-slate-900 tracking-tight leading-none">$799</span>
                      <span className="text-xs text-slate-500 ml-1">/mo</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Software platform fee</p>
                  </>
                ) : (
                  <>
                    {softwareFeeWaivedFirst12Months ? (
                      <div className="flex items-center gap-3">
                        <div className="flex items-baseline">
                          <span className="text-2xl font-extrabold text-slate-900 tracking-tight leading-none">$0</span>
                          <span className="text-xs text-slate-500 ml-1">/mo</span>
                        </div>
                        <div className="w-px h-5 bg-slate-200" />
                        <span className="text-base font-semibold text-slate-300 line-through">$799/mo</span>
                      </div>
                    ) : (
                      <div className="flex items-baseline">
                        <span className="text-2xl font-extrabold text-slate-900 tracking-tight leading-none">$799</span>
                        <span className="text-xs text-slate-500 ml-1">/mo</span>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-500 mt-1">{softwarePricingNote}</p>
                  </>
                )}
              </div>

              {/* Row 3: Value banners */}
              <div className="px-5 pb-2.5 border-r border-slate-100">
                <div className="bg-slate-50 rounded-xl px-4 py-2 border border-slate-200">
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    {isManaged ? (
                      <>
                        Includes <strong className="text-slate-900">20 AI products + dedicated human billing expert</strong> who
                        reviews claims, EOBs, reconciliation, and provides full strategic consulting on your revenue cycle.
                        AI does the heavy lifting — your expert fills gaps and improves your NCR.
                      </>
                    ) : (
                      <>
                        Your staff costs <strong className="text-slate-900">$120+ per patient journey</strong> to do less work,
                        at lower quality, with errors that result in revenue loss. Our AI does it for{" "}
                        <strong className="text-slate-900">1/10th–1/15th the cost</strong> — faster, more accurately,
                        and around the clock.
                      </>
                    )}
                  </p>
                </div>
              </div>
              <div className="px-5 pb-2.5">
                <div className="bg-slate-50 rounded-xl px-4 py-2 border border-slate-200">
                  <p className="text-[11px] text-slate-600 leading-relaxed">
                    Replaces a software stack that would otherwise cost you{" "}
                    <strong className="text-slate-900">~$3,700/mo</strong> to piecemeal across{" "}
                    <strong className="text-slate-900">10+ vendors</strong> — PMS, imaging, comms, marketing,
                    eClaims, payments, and more.
                  </p>
                </div>
              </div>

              {/* Row 4: Product / software lists */}
              <div className="px-5 pt-2.5 pb-3 border-r border-slate-100 border-t border-t-slate-100 overflow-y-auto">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  20 AI products included
                </p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                  {aiProducts.map((product, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                      <span className="text-[11px] text-slate-600">{product}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-5 pt-2.5 pb-3 border-t border-t-slate-100 overflow-y-auto">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  10 softwares replaced
                </p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  {softwareItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                      <span className="text-[11px] text-slate-600">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="border-t border-slate-100 px-5 py-2 flex items-center justify-between flex-shrink-0">
              <p className="text-[10px] text-slate-500">
                Pricing is per location — you cannot aggregate volume across locations.
              </p>
              <p className="text-[10px] text-slate-500">
                AI costs scale with patient volume · Software cost is fixed
              </p>
            </div>

          </motion.div>

        {/* Add-ons — managed only, vertical column beside main card */}
        {isManaged && (
          <motion.div
            initial={{ opacity: 0, x: 15 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="w-[210px] flex-shrink-0 flex flex-col gap-2.5 self-stretch"
          >
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Optional Add-ons</p>
            <div className="bg-white rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[12px] font-bold text-slate-800">Human Denial Mgmt</span>
                <span className="text-[11px] font-bold text-blue-600">+1%</span>
              </div>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                Dedicated denial specialist reviews every denied claim, drafts appeals, follows up with payers, and tracks through resolution.
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[12px] font-bold text-slate-800">SOTA Cloud Imaging</span>
                <span className="text-[11px] font-bold text-blue-600">$159/mo</span>
              </div>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                AI-powered cloud imaging with automatic caries detection, perio analysis, and bone loss measurement.
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[12px] font-bold text-slate-800">PMS Migration</span>
                <span className="text-[11px] font-bold text-blue-600">$399/mo</span>
              </div>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                Seamless data migration from Dentrix, Eaglesoft, and Open Dental into Asha PMS.
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default QuoteEngagementModelsSlide;
